// internal/SecureWelcomePage.jsx — AYA Secure Welcome (maquette) : enrôlement conversationnel, vérifications temps réel, passkey, codes recovery, brief.
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api/internal`;
const H = () => ({ "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("civ_user_token") || ""}` });
const G = "#2EE27B";

async function tryPasskey(name) {
  try {
    if (!window.PublicKeyCredential) return false;
    const cred = await navigator.credentials.create({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: { name: "AYA Internal" },
        user: { id: crypto.getRandomValues(new Uint8Array(16)), name: name || "aya-user", displayName: name || "AYA" },
        pubKeyCredParams: [{ type: "public-key", alg: -7 }, { type: "public-key", alg: -257 }],
        timeout: 30000, authenticatorSelection: { userVerification: "preferred" },
      },
    });
    return !!cred;
  } catch { return false; }
}

const Check = ({ ok, label, pending }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 12px", borderRadius: 11, background: "rgba(255,255,255,0.03)", border: `1px solid ${ok ? "rgba(46,226,123,0.35)" : "rgba(255,255,255,0.08)"}` }}>
    <span style={{ width: 20, height: 20, borderRadius: 99, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, background: ok ? G : "rgba(255,255,255,0.08)", color: ok ? "#04120B" : "#8BA394" }}>{ok ? "✓" : pending ? "…" : "·"}</span>
    <span style={{ fontSize: 12.5, fontWeight: 700, color: ok ? "#EAFDF3" : "#8BA394" }}>{label}</span>
  </div>
);

export default function SecureWelcomePage() {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [me, setMe] = useState(null);
  const [step, setStep] = useState(0);
  const [checks, setChecks] = useState({ device: false, session: false, context: false, presence: false });
  const [presenceOn, setPresenceOn] = useState(false);
  const [passkeyOk, setPasskeyOk] = useState(null);
  const [codes, setCodes] = useState(null);
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => { if (!isAuthenticated) navigate("/login?next=/welcome"); }, [isAuthenticated, navigate]);
  useEffect(() => {
    fetch(`${API}/identity/me`, { headers: H() }).then((r) => r.json()).then((d) => {
      if (!d.ok) { navigate("/internal"); return; }
      setMe(d);
      setStep(1);
    }).catch(() => navigate("/internal"));
    return () => { try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {} };
  }, [navigate]);

  // Vérifications temps réel animées.
  useEffect(() => {
    if (step !== 2) return;
    const t1 = setTimeout(() => setChecks((c) => ({ ...c, device: true })), 700);
    const t2 = setTimeout(() => setChecks((c) => ({ ...c, session: true })), 1500);
    const t3 = setTimeout(() => setChecks((c) => ({ ...c, context: true })), 2300);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, [step]);

  const startPresence = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
      setPresenceOn(true);
      setTimeout(() => setChecks((c) => ({ ...c, presence: true })), 2000);
    } catch { toast.error("Caméra indisponible — la vérification de présence est optionnelle (Recovery disponible)."); }
  };

  const doPasskey = async () => {
    setBusy(true);
    const ok = await tryPasskey(me?.display_name);
    setPasskeyOk(ok);
    if (!ok) toast.info("Passkey indisponible dans cet environnement — vous pourrez l'ajouter plus tard.");
    setBusy(false);
  };

  const enroll = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/identity/enroll`, { method: "POST", headers: H(), body: JSON.stringify({ device_label: "Appareil principal", passkey: !!passkeyOk, face_consent: presenceOn, presence_verified: checks.presence }) });
      const d = await r.json();
      if (!d.ok) throw new Error(d.error);
      setCodes(d.recovery_codes);
      try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch {}
      setStep(4);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setBusy(false); }
  };

  const goWorkspace = () => navigate(me?.ceo ? "/internal" : `/internal/ws/${me?.workspace?.slug || ""}`);
  const name = me?.display_name || (me?.ceo ? "Cédric" : "");
  const already = me?.enrolled;

  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(ellipse at 30% 0%, #0E1A13 0%, #060A08 60%)", color: "#EAFDF3", fontFamily: "Inter, system-ui, sans-serif", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }} data-testid="secure-welcome">
      <div style={{ width: "100%", maxWidth: 520 }}>
        <div style={{ textAlign: "center", marginBottom: 22 }}>
          <div style={{ width: 74, height: 74, margin: "0 auto 12px", borderRadius: 99, background: "radial-gradient(circle at 35% 30%, rgba(46,226,123,0.5), rgba(46,226,123,0.08) 65%)", border: "1px solid rgba(46,226,123,0.45)", boxShadow: "0 0 50px rgba(46,226,123,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <img src="/aya/splash.png" alt="AYA" style={{ width: 44, height: 44, objectFit: "contain" }} />
          </div>
          <div style={{ fontSize: 26, fontWeight: 900 }}>AYA <span style={{ color: G }}>Secure Welcome</span></div>
          <div style={{ fontSize: 11.5, color: "#8BA394", marginTop: 3 }}>Accès sans mot de passe · sécurisé, humain et intelligent</div>
          {me && <span style={{ display: "inline-block", marginTop: 8, fontSize: 9, fontWeight: 900, letterSpacing: 1, padding: "3px 10px", borderRadius: 999, border: `1px solid ${me.auth_mode === "experimental" ? "rgba(232,184,75,0.5)" : "rgba(46,226,123,0.5)"}`, color: me.auth_mode === "experimental" ? "#E8B84B" : G }} data-testid="welcome-auth-mode">MODE {me.auth_mode === "experimental" ? "EXPERIMENTAL" : "PRODUCTION"}</span>}
        </div>

        <div style={{ background: "rgba(16,24,19,0.9)", border: "1px solid rgba(46,226,123,0.18)", borderRadius: 20, padding: 22, boxShadow: "0 24px 70px rgba(0,0,0,0.5)" }}>
          {step === 1 && me && (
            <div data-testid="welcome-step-greeting">
              <div style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.6 }}>
                Bonjour {name} 👋<br />
                {already ? "Ravi de te revoir." : <>Bienvenue dans AYA.<br />Je vais prendre quelques instants pour sécuriser ton environnement.</>}
              </div>
              <button data-testid="welcome-continue" onClick={() => setStep(2)} style={{ marginTop: 18, width: "100%", padding: "13px 16px", borderRadius: 13, border: "none", background: `linear-gradient(180deg,${G},#1CA95B)`, color: "#04120B", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                {already ? "Vérifier mon environnement →" : "Commencer la sécurisation →"}
              </button>
            </div>
          )}

          {step === 2 && (
            <div data-testid="welcome-step-checks">
              <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 12 }}>🛰 Vérifications en temps réel</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                <Check ok={checks.device} pending label="Appareil — reconnu et conforme" />
                <Check ok={checks.session} pending label="Session — intégrité vérifiée" />
                <Check ok={checks.context} pending label="Contexte réseau — sécurisé" />
                {me?.auth_mode === "experimental" && (
                  <div style={{ borderRadius: 11, border: "1px dashed rgba(232,184,75,0.4)", padding: 10 }}>
                    <div style={{ fontSize: 10, fontWeight: 900, color: "#E8B84B", letterSpacing: 0.8, marginBottom: 6 }}>EXPERIMENTAL · Présence caméra (traitement 100% local, aucune image envoyée ni stockée)</div>
                    {!presenceOn ? (
                      <button data-testid="welcome-presence-btn" onClick={startPresence} style={{ padding: "8px 14px", borderRadius: 10, border: "1px solid rgba(232,184,75,0.5)", background: "rgba(232,184,75,0.08)", color: "#E8B84B", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}>📷 Activer la vérification de présence</button>
                    ) : (
                      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                        <video ref={videoRef} autoPlay muted playsInline style={{ width: 86, height: 64, borderRadius: 10, objectFit: "cover", border: `1px solid ${checks.presence ? G : "rgba(255,255,255,0.15)"}` }} />
                        <Check ok={checks.presence} pending label={checks.presence ? "Présence réelle confirmée" : "Analyse de présence…"} />
                      </div>
                    )}
                  </div>
                )}
              </div>
              <button data-testid="welcome-checks-next" disabled={!checks.context} onClick={() => (already ? setStep(5) : setStep(3))}
                style={{ marginTop: 16, width: "100%", padding: "12px 16px", borderRadius: 13, border: "none", background: checks.context ? `linear-gradient(180deg,${G},#1CA95B)` : "rgba(255,255,255,0.08)", color: checks.context ? "#04120B" : "#8BA394", fontWeight: 900, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>
                Continuer →
              </button>
            </div>
          )}

          {step === 3 && (
            <div data-testid="welcome-step-passkey">
              <div style={{ fontSize: 13.5, fontWeight: 800, marginBottom: 6 }}>🔑 Passkey — ta biométrie, gérée par ton appareil</div>
              <div style={{ fontSize: 12, color: "#8BA394", lineHeight: 1.6, marginBottom: 14 }}>Face ID, empreinte ou clé de sécurité : la vérification reste sur TON appareil (WebAuthn). AYA ne stocke aucune donnée biométrique.</div>
              <button data-testid="welcome-passkey-btn" onClick={doPasskey} disabled={busy || passkeyOk === true}
                style={{ width: "100%", padding: "12px 16px", borderRadius: 13, border: `1px solid ${passkeyOk ? G : "rgba(46,226,123,0.4)"}`, background: passkeyOk ? "rgba(46,226,123,0.14)" : "rgba(255,255,255,0.04)", color: passkeyOk ? G : "#EAFDF3", fontWeight: 800, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>
                {passkeyOk === true ? "✓ Passkey enregistrée" : busy ? "Création en cours…" : "Créer ma passkey"}
              </button>
              <button data-testid="welcome-enroll" onClick={enroll} disabled={busy}
                style={{ marginTop: 10, width: "100%", padding: "13px 16px", borderRadius: 13, border: "none", background: `linear-gradient(180deg,${G},#1CA95B)`, color: "#04120B", fontWeight: 900, fontSize: 14, cursor: "pointer", fontFamily: "inherit" }}>
                {busy ? "Sécurisation…" : "🔐 Sécuriser mon identité"}
              </button>
              {passkeyOk === false && <div style={{ fontSize: 11, color: "#8BA394", marginTop: 8 }}>Passkey indisponible ici — l'enrôlement continue avec appareil + session + Recovery.</div>}
            </div>
          )}

          {step === 4 && codes && (
            <div data-testid="welcome-step-codes">
              <div style={{ fontSize: 15, fontWeight: 900, color: G }}>✓ Ton identité {me?.ceo ? "Founder" : ""} est sécurisée.</div>
              <div style={{ fontSize: 12, color: "#8BA394", margin: "8px 0 12px" }}>Codes de récupération — affichés UNE SEULE FOIS. Conserve-les en lieu sûr ({me?.ceo ? "Founder Recovery Vault" : "AYA Recovery"}).</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 7 }} data-testid="welcome-recovery-codes">
                {codes.map((c) => <span key={c} style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 800, textAlign: "center", padding: "8px 4px", borderRadius: 9, background: "#0D1310", border: "1px solid rgba(46,226,123,0.25)", color: "#EAFDF3" }}>{c}</span>)}
              </div>
              <button data-testid="welcome-codes-done" onClick={() => setStep(5)} style={{ marginTop: 14, width: "100%", padding: "12px 16px", borderRadius: 13, border: "none", background: `linear-gradient(180deg,${G},#1CA95B)`, color: "#04120B", fontWeight: 900, fontSize: 13.5, cursor: "pointer", fontFamily: "inherit" }}>J'ai conservé mes codes →</button>
            </div>
          )}

          {step === 5 && me && (
            <div data-testid="welcome-step-brief">
              <div style={{ fontSize: 16, fontWeight: 900 }}>Bonjour {name}. {already ? "Ravi de te revoir." : ""}</div>
              <div style={{ fontSize: 12.5, color: "#8BA394", margin: "4px 0 14px" }}>Tout est sécurisé. {me.ceo ? "Ton AYA Command Center est prêt." : `Ton espace ${me.workspace?.name || ""} est prêt.`}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }} data-testid="welcome-brief">
                {[[me.brief.priority_tasks, "tâches prioritaires", "📋"], [me.brief.alerts, "alertes", "🚨"], [me.brief.pending_validations, "validations en attente", "🔐"]].map(([v, l, i]) => (
                  <div key={l} style={{ textAlign: "center", padding: "12px 6px", borderRadius: 12, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(46,226,123,0.18)" }}>
                    <div style={{ fontSize: 20 }}>{i}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: G }}>{v}</div>
                    <div style={{ fontSize: 9.5, color: "#8BA394" }}>{l}</div>
                  </div>
                ))}
              </div>
              <button data-testid="welcome-enter" onClick={goWorkspace} style={{ marginTop: 16, width: "100%", padding: "14px 16px", borderRadius: 13, border: "none", background: `linear-gradient(180deg,${G},#1CA95B)`, color: "#04120B", fontWeight: 900, fontSize: 14.5, cursor: "pointer", fontFamily: "inherit" }}>
                Accéder à mon espace AYA →
              </button>
            </div>
          )}
        </div>
        <div style={{ textAlign: "center", fontSize: 10, color: "#5C6E66", marginTop: 14 }}>🔒 Zero Trust · Passkey natif · Aucune donnée biométrique stockée · RGPD & lois locales</div>
      </div>
    </div>
  );
}
